import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar, Building2, User, DollarSign, Eye, CheckCircle2 } from "lucide-react";
import type { BookingResponseUI } from "@/types/activities";

// Utility functions for booking display
const getTypeColor = (type: string) => {
  switch (type) {
    case "marketplace": return "bg-green-100 text-green-800 dark:bg-green-900 dark:bg-opacity-20 dark:text-green-400";
    default: return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300";
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case "pending": return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:bg-opacity-20 dark:text-blue-400";
    case "negotiating": return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:bg-opacity-20 dark:text-yellow-400";
    case "awaiting-approval": return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:bg-opacity-20 dark:text-purple-400";
    case "vendor-booked": return "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:bg-opacity-20 dark:text-orange-400";
    case "confirmed": return "bg-green-100 text-green-800 dark:bg-green-900 dark:bg-opacity-20 dark:text-green-400";
    case "cancelled": return "bg-red-100 text-red-800 dark:bg-red-900 dark:bg-opacity-20 dark:text-red-400";
    case "rejected": return "bg-red-100 text-red-800 dark:bg-red-900 dark:bg-opacity-20 dark:text-red-400";
    case "expired": return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:bg-opacity-20 dark:text-gray-400";
    default: return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300";
  }
};

interface BookingCardProps {
  booking: BookingResponseUI;
  selected: boolean;
  onSelect: (selected: boolean) => void;
  onOpen: () => void;
}

export default function BookingCard({ booking, selected, onSelect, onOpen }: BookingCardProps) {
  return (
    <Card className="athlyn-card transition-all hover:shadow-md cursor-pointer" onClick={onOpen}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3 flex-1">
            <div onClick={(e) => e.stopPropagation()}>
              <Checkbox 
                checked={selected}
                onCheckedChange={onSelect}
                aria-label={`Select ${booking.title}`}
              />
            </div>
            <img 
              src={booking.organizationLogo} 
              alt={booking.organizationName}
              className="w-8 h-8 rounded-full flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-sm truncate">{booking.title}</h3>
              <p className="text-xs text-muted-foreground">{booking.organizationName}</p>
            </div>
          </div>
          <div className="flex items-center gap-1 ml-2">
            {booking.status === 'confirmed' && <CheckCircle2 className="w-4 h-4 text-green-500" />}
            <Badge variant="outline" className={`${getStatusColor(booking.status)} text-xs`}>
              {booking.status.replace(/-/g, ' ')}
            </Badge>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0 space-y-3">
        {/* Type Badge */}
        <div>
          <Badge variant="outline" className={`${getTypeColor(booking.type)} text-xs`}>
            {booking.type}
          </Badge>
        </div>

        {/* Description */}
        {booking.description && (
          <p className="text-sm text-muted-foreground line-clamp-2">
            {booking.description}
          </p>
        )}

        {/* Details Grid */}
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div className="flex items-center gap-1 text-muted-foreground">
            <Calendar className="w-3 h-3" />
            <span>{new Date(booking.dateSubmitted).toLocaleDateString()}</span>
          </div>
          
          {booking.vendorName && (
            <div className="flex items-center gap-1 text-muted-foreground">
              <User className="w-3 h-3" />
              <span className="truncate">{booking.vendorName}</span>
            </div>
          )}

          {booking.preferredDatesFormatted && (
            <div className="flex items-center gap-1 text-muted-foreground col-span-2">
              <Calendar className="w-3 h-3" />
              <span className="truncate">Preferred: {booking.preferredDatesFormatted}</span>
            </div>
          )}
        </div>

        {/* Vendor and Price Info */}
        {booking.vendor && (
          <div className="p-2 bg-muted/30 rounded-md">
            <div className="flex justify-between items-center text-xs">
              <div>
                <p className="font-medium">{booking.vendor.name}</p>
                <p className="text-muted-foreground">{booking.vendor.package}</p>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-1 text-sm font-medium">
                  <DollarSign className="w-3 h-3" />
                  {booking.vendor.price}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Button */}
        <div className="pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onOpen();
            }}
            className="w-full flex items-center gap-2"
          >
            <Eye className="w-4 h-4" />
            View Details
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}